import React, { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import MythosLogo, { MYTHOS_URL, MYTHOS_CIN, MYTHOS_CONTACT_URL } from "../components/MythosLogo";
import { ArrowRight, Sparkles, BookOpen, Vault, Globe2, Cpu, Mail, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Ecosystem() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errMsg, setErrMsg] = useState("");

  const submitWaitlist = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      setStatus("error");
      setErrMsg("Please enter a valid email.");
      return;
    }
    setStatus("loading");
    setErrMsg("");
    try {
      await axios.post(`${API}/subscribe`, {
        email,
        product: "lifevault",
        source: "ecosystem-page",
      });
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setErrMsg(err?.response?.data?.detail || "Could not save. Try again.");
    }
  };

  return (
    <div data-testid="ecosystem-page">
      {/* Master brand hero — restrained, dark, gold */}
      <section className="mythos-ink-bg" data-testid="ecosystem-hero">
        <div className="max-w-7xl mx-auto px-5 md:px-10 py-20 md:py-28">
          <a
            href={MYTHOS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 mb-8 group hover:opacity-90 transition"
            data-testid="ecosystem-mythos-link"
            title="Visit Mythos India Studios corporate website"
          >
            <MythosLogo size={44} testid="ecosystem-mythos-logo" />
            <div className="flex flex-col leading-tight">
              <span className="font-mythos text-xs tracking-[0.22em] uppercase mythos-gold font-semibold" data-testid="mythos-brandline">
                Mythos India Studios
              </span>
              <span className="text-[10px] tracking-[0.18em] uppercase text-[#F4EFE6]/50 font-mono">
                mythosindiastudio.com ↗
              </span>
            </div>
          </a>
          <h1 className="font-mythos text-4xl md:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.04] max-w-4xl">
            India's intelligence,
            <br />
            <span className="mythos-gold">reimagined.</span>
          </h1>
          <p className="font-mythos text-lg md:text-xl text-[#E8EBF0]/80 mt-6 max-w-2xl">
            An AI-first cultural technology company turning India's deepest knowledge
            into the world's most personal learning experience.
          </p>
          <div className="mt-10 flex flex-wrap gap-3" data-testid="ecosystem-hero-cta">
            <a href="#platforms" className="inline-flex items-center gap-2 mythos-gold-bg text-[#0E0F12] rounded-full px-6 py-3 font-semibold transition hover:opacity-90" data-testid="ecosystem-cta-platforms">
              Explore the platforms <ArrowRight size={16} />
            </a>
            <a href="#story" className="inline-flex items-center gap-2 border border-[#3A3D44] hover:border-[#C9A66B] text-[#F4EFE6] rounded-full px-6 py-3 font-semibold transition" data-testid="ecosystem-cta-story">
              Read our story
            </a>
          </div>
        </div>
      </section>

      {/* PLATFORMS */}
      <section id="platforms" className="max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-20" data-testid="ecosystem-platforms">
        <div className="text-[11px] tracking-[0.24em] uppercase mythos-gold font-bold mb-3" style={{ color: "#9A7A3F" }}>The ecosystem</div>
        <h2 className="font-mythos text-3xl md:text-4xl font-bold tracking-tight max-w-2xl">Two platforms. One mission.</h2>
        <p className="text-[#4A4A4A] mt-3 max-w-2xl">
          Make Indian intelligence — ancient and modern — instantly accessible to every learner, parent, and creator on earth.
        </p>

        <div className="grid md:grid-cols-2 gap-6 mt-10">
          {/* EduSphere card */}
          <Link to="/" className="card p-7 md:p-9 group" data-testid="platform-edusphere-card">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-12 h-12 rounded-xl bg-[#FBE7DF] grid place-items-center text-[#E07A5F]">
                <BookOpen size={22} />
              </div>
              <div>
                <div className="font-heading text-xl font-extrabold text-[#1A1A1A]">EduSphere</div>
                <div className="text-[10px] tracking-[0.22em] uppercase text-[#3A5A40] font-semibold">Learning · Live now</div>
              </div>
            </div>
            <p className="text-[#1A1A1A] leading-relaxed">
              The AI companion that teaches mythology, STEM, and life skills through stories — in any Indian language.
            </p>
            <div className="flex flex-wrap gap-1.5 mt-4">
              {["Mythology", "STEM models", "Quizzes", "Learning paths", "Multilingual"].map((t) => (
                <span key={t} className="text-[10px] tracking-[0.12em] uppercase font-semibold bg-[#F4F1EA] border border-[#DCD7CE] rounded-full px-2.5 py-1 text-[#3A5A40]">{t}</span>
              ))}
            </div>
            <div className="mt-6 inline-flex items-center gap-1.5 text-sm font-bold text-[#E07A5F]">
              Open EduSphere <ArrowRight size={14} />
            </div>
          </Link>

          {/* LifeVault card — coming soon */}
          <div className="vault-card p-7 md:p-9 rounded-2xl relative overflow-hidden" data-testid="platform-lifevault-card">
            <div className="absolute top-4 right-4 vault-amber-bg text-[#0F1623] text-[10px] tracking-[0.2em] uppercase font-bold px-2.5 py-1 rounded-full" data-testid="lifevault-badge">
              Coming 2026
            </div>
            <div className="flex items-center gap-3 mb-5">
              <div className="w-12 h-12 rounded-xl bg-[#0F1623] border border-[#2A3349] grid place-items-center vault-amber">
                <Vault size={22} />
              </div>
              <div>
                <div className="font-mythos text-xl font-extrabold text-[#E8EBF0]">LifeVault</div>
                <div className="text-[10px] tracking-[0.22em] uppercase font-semibold vault-amber">Personal AI · Memory</div>
              </div>
            </div>
            <p className="text-[#E8EBF0]/85 leading-relaxed">
              Your personal AI vault for life's stories, values, and family heritage — searchable, shareable, eternal.
            </p>
            <div className="flex flex-wrap gap-1.5 mt-4">
              {["Memory journals", "AI vault search", "Voice memories", "Family heritage", "Digital legacy"].map((t) => (
                <span key={t} className="text-[10px] tracking-[0.12em] uppercase font-semibold bg-[#1A2436] border border-[#2A3349] rounded-full px-2.5 py-1 vault-amber">{t}</span>
              ))}
            </div>
            <form
              className="mt-6 flex flex-col sm:flex-row gap-2"
              onSubmit={submitWaitlist}
              data-testid="lifevault-waitlist"
            >
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={status === "success" || status === "loading"}
                placeholder="your@email.com — join the waitlist"
                className="flex-1 bg-[#0F1623] border border-[#2A3349] focus:border-[#E9B45A] outline-none rounded-full px-4 py-2.5 text-[#E8EBF0] placeholder-[#7d8294] disabled:opacity-60"
                data-testid="lifevault-waitlist-email"
              />
              <button
                type="submit"
                disabled={status === "loading" || status === "success"}
                className="vault-amber-bg text-[#0F1623] rounded-full px-5 py-2.5 font-semibold whitespace-nowrap inline-flex items-center justify-center gap-2 hover:opacity-90 transition disabled:opacity-70"
                data-testid="lifevault-waitlist-submit"
              >
                {status === "success" ? (<><Check size={14} /> Joined</>) : status === "loading" ? "Saving…" : (<><Mail size={14} /> Notify me</>)}
              </button>
            </form>
            {status === "success" && (
              <p className="text-xs vault-amber mt-3" data-testid="lifevault-waitlist-success">
                You're on the list. We'll write to you when LifeVault opens.
              </p>
            )}
            {status === "error" && (
              <p className="text-xs text-[#ff9b9b] mt-3" data-testid="lifevault-waitlist-error">{errMsg}</p>
            )}
          </div>
        </div>
      </section>

      {/* STORY */}
      <section id="story" className="mythos-ink-bg" data-testid="ecosystem-story">
        <div className="max-w-4xl mx-auto px-5 md:px-10 py-20 md:py-28">
          <div className="text-[11px] tracking-[0.24em] uppercase mythos-gold font-bold mb-4">Our story</div>
          <p className="font-mythos text-2xl md:text-3xl leading-[1.45] text-[#F4EFE6]/95 max-w-3xl">
            India is the world's oldest continuously-living civilisation, with a depth of stories,
            science, and lived wisdom unmatched anywhere on earth.
          </p>
          <p className="font-mythos text-2xl md:text-3xl leading-[1.45] text-[#F4EFE6]/70 mt-6 max-w-3xl">
            <span className="mythos-gold">Mythos India Studios</span> was founded to translate that
            inheritance into the language of the next century — AI, immersive media, and globally
            scalable products.
          </p>
          <p className="font-mythos text-2xl md:text-3xl leading-[1.45] text-[#F4EFE6]/70 mt-6 max-w-3xl">
            We don't just digitise culture. <span className="text-[#F4EFE6]">We give it new flight.</span>
          </p>
        </div>
      </section>

      {/* PILLARS */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 py-16 md:py-20" data-testid="ecosystem-pillars">
        <div className="text-[11px] tracking-[0.24em] uppercase font-bold mb-3" style={{ color: "#9A7A3F" }}>Our principles</div>
        <h3 className="font-mythos text-3xl md:text-4xl font-bold tracking-tight max-w-2xl">Indian intelligence. Premium silence. Globally investable.</h3>
        <div className="grid md:grid-cols-3 gap-5 mt-10">
          {[
            { icon: Cpu, title: "AI-first", body: "Every surface uses AI invisibly to make answers better, faster, and more personal — never as a 'powered by' badge." },
            { icon: Sparkles, title: "Cultural authenticity", body: "Heritage as substance, not costume. Mythology rendered with the dignity of a National Geographic special." },
            { icon: Globe2, title: "Built for the world", body: "Designed from day one for global edtech, OTT, and AI-assistant markets. Multilingual: 12 languages by 2027." },
          ].map((p, i) => (
            <div key={i} className="card p-6" data-testid={`ecosystem-pillar-${i}`}>
              <div className="w-11 h-11 rounded-xl bg-[#0E0F12] grid place-items-center mythos-gold mb-4">
                <p.icon size={20} />
              </div>
              <div className="font-heading font-bold text-lg">{p.title}</div>
              <div className="text-sm text-[#4A4A4A] mt-2">{p.body}</div>
            </div>
          ))}
        </div>
      </section>

      {/* INVESTOR LINE */}
      <section className="max-w-7xl mx-auto px-5 md:px-10 pb-24" data-testid="ecosystem-investor-line">
        <div className="card p-8 md:p-10 grid md:grid-cols-[auto_1fr] gap-6 items-center">
          <a href={MYTHOS_URL} target="_blank" rel="noopener noreferrer" data-testid="ecosystem-investor-logo-link" className="hover:opacity-80 transition">
            <MythosLogo size={64} testid="ecosystem-investor-logo" />
          </a>
          <div>
            <div className="text-[11px] tracking-[0.22em] uppercase font-bold mb-1" style={{ color: "#9A7A3F" }}>For investors</div>
            <p className="font-mythos text-lg md:text-xl text-[#1A1A1A] max-w-3xl">
              <a href={MYTHOS_URL} target="_blank" rel="noopener noreferrer" className="font-bold hover:text-[#9A7A3F] transition" data-testid="ecosystem-investor-mythos-link">Mythos India Studios</a> is building the operating system for Indian intelligence — across two
              AI-first platforms (<strong>EduSphere</strong> for learning, <strong>LifeVault</strong> for legacy)
              with shared infrastructure, shared brand equity, and shared India-to-world distribution.
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-[#4A4A4A]">
              <span className="font-mono" data-testid="ecosystem-cin">CIN: {MYTHOS_CIN}</span>
              <a
                href={MYTHOS_CONTACT_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 mythos-gold-bg text-[#0E0F12] rounded-full px-3.5 py-1.5 font-semibold hover:opacity-90 transition"
                data-testid="ecosystem-investor-contact"
              >
                Contact investor relations ↗
              </a>
              <a href={MYTHOS_URL} target="_blank" rel="noopener noreferrer" className="hover:opacity-80 font-semibold" data-testid="ecosystem-corporate-cta" style={{ color: "#9A7A3F" }}>
                mythosindiastudio.com ↗
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
